import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicIcon from '@mui/icons-material/Public';
import LocationCityIcon from '@mui/icons-material/LocationCity';
// InsightsIcon removed (summary panel deleted)
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
// Link not used directly here; SourceLink handles links
import SourceLink from '../components/SourceLink';
import { formatEpochLocal } from '../utils/helpers';
import { fetchData, fetchUserDetails } from '../services/api';
import { useQuery } from '@tanstack/react-query';
import { useDeviceCount } from '../contexts/DeviceCountContext';
// type UnifiedDeviceInfo import removed (unused)
// PieChart removed (using horizontal bars instead)

function StatCard({ title, value, tall = false, icon, color = '#1976d2', vertical = false }: { title: string; value: React.ReactNode; tall?: boolean; icon?: React.ReactNode; color?: string; vertical?: boolean }) {
  if (vertical) {
    return (
      <Paper sx={{ p: 1.5, borderRadius: 2, minHeight: tall ? 160 : 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }} elevation={1}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 72, height: 72, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${color}22 0%, ${color}66 100%)`, boxShadow: `0 6px 18px ${color}22` }}>
            {icon ? React.cloneElement(icon as any, { sx: { color: '#fff', fontSize: 34 } }) : null}
          </Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>{title}</Typography>
          <Typography variant="h4">{value}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 1.5, borderRadius: 2, minHeight: tall ? 160 : 'auto' }} elevation={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ width: 72, height: 72, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `linear-gradient(135deg, ${color}22 0%, ${color}66 100%)`, boxShadow: `0 6px 18px ${color}22` }}>
          {icon ? React.cloneElement(icon as any, { sx: { color: '#fff', fontSize: 34 } }) : null}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function Dashboard() {
  const { setTgmaxxCount } = useDeviceCount();
  
  // Get user details to access user tenants
  const { data: userDetails } = useQuery({
    queryKey: ['user', 'details'],
    queryFn: fetchUserDetails,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Extract user tenants
  const userTenants = React.useMemo(() => {
    if (!userDetails?.userTenants) return [];
    return userDetails.userTenants.filter((ut: any) => 
      ut?.tenant?.name && (ut.tenant.name.toLowerCase() === 'tgstream' || ut.tenant.name.toLowerCase() === 'tgmaxx' || ut.tenant.name.toLowerCase() === 'tgstreamtest' || ut.tenant.name.toLowerCase() === 'tgstreamtl4kbr')
    );
  }, [userDetails]);

  // Fetch summary data for each tenant (using fixed hooks)
  const tgstreamQuery = useQuery({
    queryKey: ['tenant-summary', 'tgstream'],
    queryFn: async () => fetchData('api/tgstream/summary'),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: userTenants.some((ut: any) => ut?.tenant?.name?.toLowerCase() === 'tgstream'),
  });

  const tgstreamtestQuery = useQuery({
    queryKey: ['tenant-summary', 'tgstreamtest'],
    queryFn: async () => fetchData('api/tgstream/summary'),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: userTenants.some((ut: any) => ut?.tenant?.name?.toLowerCase() === 'tgstreamtest'),
  });

  const tgstreamtl4kbrQuery = useQuery({
    queryKey: ['tenant-summary', 'tgstreamtl4kbr'],
    queryFn: async () => fetchData('api/tgstream/summary'),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: userTenants.some((ut: any) => ut?.tenant?.name?.toLowerCase() === 'tgstreamtl4kbr'),
  });

  const tgmaxxQuery = useQuery({
    queryKey: ['tenant-summary', 'tgmaxx'],
    queryFn: async () => fetchData('api/tgmaxx/summary'),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: userTenants.some((ut: any) => ut?.tenant?.name?.toLowerCase() === 'tgmaxx'),
  });

  // Create tenantQueries array for compatibility
  const tenantQueries = React.useMemo(() => {
    return userTenants.map((ut: any) => {
      const tenantName = ut.tenant.name.toLowerCase();
      return tenantName === 'tgmaxx' ? tgmaxxQuery : 
             tenantName === 'tgstreamtest' ? tgstreamtestQuery :
             tenantName === 'tgstreamtl4kbr' ? tgstreamtl4kbrQuery :
             tgstreamQuery;
    });
  }, [userTenants, tgmaxxQuery, tgstreamtestQuery, tgstreamtl4kbrQuery, tgstreamQuery]);

  // State for tabs
  const [recentlyActivatedTab, setRecentlyActivatedTab] = React.useState(0);
  const [firmwareTab, setFirmwareTab] = React.useState(0);

  // Store TGMaxx device count in memory when fetched
  React.useEffect(() => {
    if (tgmaxxQuery.data?.totalDevices && typeof tgmaxxQuery.data.totalDevices === 'number') {
      setTgmaxxCount(tgmaxxQuery.data.totalDevices);
    }
  }, [tgmaxxQuery.data?.totalDevices, setTgmaxxCount]);

  // Calculate combined totals
  const { total, online, activated, bySource } = React.useMemo(() => {
    let totalDevices = 0;
    let onlineDevices = 0;
    let activatedDevices = 0;
    const sourceMap: Record<string, number> = {};

    tenantQueries.forEach((query: any, index: number) => {
      if (query.data) {
        const data = query.data;
        const tenantName = userTenants[index]?.tenant?.displayName || userTenants[index]?.tenant?.name;
        
        totalDevices += data.totalDevices || 0;
        onlineDevices += data.onlineDevices || 0;
        activatedDevices += data.activatedDevices || 0;
        
        // Add to source breakdown
        if (tenantName) {
          sourceMap[tenantName] = (sourceMap[tenantName] || 0) + (data.totalDevices || 0);
        }
      }
    });

    const bySource = Object.entries(sourceMap).sort((a, b) => b[1] - a[1]);

    return {
      total: totalDevices,
      online: onlineDevices,
      activated: activatedDevices,
      bySource,
    };
  }, [tenantQueries, userTenants]);

  // Check if any queries are loading
  const loadingSummary = tenantQueries.some((q: any) => q.isLoading);

  return (
    <>

      {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}> */}
              <Typography variant="h5" className="page-title">Dashboard</Typography>
        {/* </Box> */}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'nowrap', mb: 2 }}>
        <Paper sx={{ flex: '1 1 auto', minWidth: 260, maxWidth: 'calc(100% - 440px)', p: 2, borderRadius: 2, border: '2px solid rgba(0,0,0,0.06)', minHeight: 200, display: 'flex', flexDirection: 'column' }} elevation={3}>

            
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 2fr)', gap: 2}}>
            <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 3', lg: 'span 2' } }}>
              <StatCard title="Total Devices" value={loadingSummary ? '...' : total} tall icon={<DevicesOtherIcon />} color="#6c5ce7" vertical />
            </Box>
            <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 3', lg: 'span 2' } }}>
              <StatCard title="Online Devices" value={loadingSummary ? '...' : online} tall icon={<CloudDoneIcon />} color="#00b894" vertical />
            </Box>
            <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 3', lg: 'span 2' } }}>
              <StatCard title="Activated Devices" value={loadingSummary ? '...' : activated} tall icon={<CheckCircleIcon />} color="#0984e3" vertical />
            </Box>
          </Box>
        </Paper>

  <Paper sx={{ width: 420, flex: '0 0 420px', p: 2, borderRadius: 2, border: '2px solid rgba(0,0,0,0.06)', minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center' }} elevation={2}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublicIcon sx={{ color: 'text.secondary' }} />
            Devices By Source
          </Typography>
          {bySource.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No data</Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1 }}>
              {(() => {
                const totalCount = bySource.reduce((s, [, v]) => s + v, 0) || 1;
                const palette = ['#4f83cc', '#66bb6a', '#ffb74d', '#90caf9', '#b39ddb', '#29b6f6'];
                return bySource.slice(0, 6).map(([k, v], i) => {
                  const pct = Math.round((v / totalCount) * 1000) / 10;
                  const barWidth = Math.max(40, Math.round((v / totalCount) * 275));
                  return (
                    <Tooltip key={k} title={`${v} devices • ${pct}%`}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5 }}>
                        <Box sx={{ width: barWidth, height: 14, background: palette[i % palette.length], borderRadius: 1, boxShadow: 'inset 0 -2px rgba(0,0,0,0.06)' }} />
                        <Box sx={{ minWidth: 140 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#4f83cc' }}>
                            <SourceLink source={k} sx={{ fontWeight: 600, color: '#4f83cc' }} />
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{v} • {pct}%</Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  );
                });
              })()}
            </Box>
          )}
        </Paper>
      </Box>
      
      {/* New row: Last Activated Devices + Device Software Version with Tabs */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', mb: 2 }}>
        <Paper sx={{ flex: '1 1 auto', p: 2, borderRadius: 2, border: '2px solid rgba(0,0,0,0.06)', minHeight: 200 }} elevation={3}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublicIcon sx={{ color: 'text.secondary' }} />
            Recently Activated Devices
          </Typography>
          
          {userTenants.length > 1 && (
            <Tabs 
              value={recentlyActivatedTab} 
              onChange={(_, newValue) => setRecentlyActivatedTab(newValue)}
              sx={{ mb: 2, minHeight: 'auto' }}
            >
              {userTenants.map((ut: any, index: number) => (
                <Tab 
                  key={ut.tenant.name}
                  label={ut.tenant.displayName || ut.tenant.name}
                  sx={{ textTransform: 'none', minHeight: 'auto', py: 1 }}
                />
              ))}
            </Tabs>
          )}
          
          {(() => {
            const currentQuery = tenantQueries[recentlyActivatedTab];
            const lastActivated = currentQuery?.data?.last5ActivatedDevices || [];
            
            if (lastActivated.length === 0) {
              return <Typography variant="body2" color="text.secondary">No Recent Activations</Typography>;
            }
            
            return (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>SN</TableCell>
                    <TableCell>Activation Time</TableCell>
                    <TableCell>Model</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lastActivated.slice(0, 6).map((device: any, idx: number) => (
                    <TableRow key={device.serialNumber ?? `la-${idx}`} hover>
                      <TableCell>{device.serialNumber}</TableCell>
                      <TableCell>
                        {device.activatedTime ? formatEpochLocal(new Date(device.activatedTime).getTime()) : '\u2014'}
                      </TableCell>
                      <TableCell>{device.modelName ?? '\u2014'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
        </Paper>

        <Paper sx={{ width: 420, flex: '0 0 420px', p: 2, borderRadius: 2, border: '2px solid rgba(0,0,0,0.06)', minHeight: 200 }} elevation={2}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublicIcon sx={{ color: 'text.secondary' }} />
            Firmware Distribution
          </Typography>
          
          {userTenants.length > 1 && (
            <Tabs 
              value={firmwareTab} 
              onChange={(_, newValue) => setFirmwareTab(newValue)}
              sx={{ mb: 2, minHeight: 'auto' }}
            >
              {userTenants.map((ut: any, index: number) => (
                <Tab 
                  key={ut.tenant.name}
                  label={ut.tenant.displayName || ut.tenant.name}
                  sx={{ textTransform: 'none', minHeight: 'auto', py: 1 }}
                />
              ))}
            </Tabs>
          )}
          
          {(() => {
            const currentQuery = tenantQueries[firmwareTab];
            const firmwareDistribution = currentQuery?.data?.firmwareDistribution || {};
            const bySoftware = Object.entries(firmwareDistribution).sort((a, b) => (b[1] as number) - (a[1] as number));
            
            if (bySoftware.length === 0) {
              return <Typography variant="body2" color="text.secondary">No data</Typography>;
            }
            
            const totalCount = bySoftware.reduce((s, [, v]) => s + (v as number), 0) || 1;
            const palette = ['#4f83cc', '#66bb6a', '#ffb74d', '#90caf9', '#b39ddb', '#29b6f6'];
            
            return (
              <Box sx={{ display: 'grid', gap: 1 }}>
                {bySoftware.slice(0, 6).map(([k, v], i) => {
                  const count = v as number;
                  const pct = Math.round((count / totalCount) * 1000) / 10;
                  const barWidth = Math.max(40, Math.round((count / totalCount) * 300));
                  return (
                    <Tooltip key={k} title={`${count} devices • ${pct}%`}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5 }}>
                        <Box sx={{ width: barWidth, height: 14, background: palette[i % palette.length], borderRadius: 1, boxShadow: 'inset 0 -2px rgba(0,0,0,0.06)' }} />
                        <Box sx={{ minWidth: 140 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#4f83cc' }}>{k}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{count} • {pct}%</Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            );
          })()}
        </Paper>
      </Box>


      <Paper sx={{ p: 2, borderRadius: 2, border: '2px solid rgba(0,0,0,0.06)' }} elevation={3}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationCityIcon sx={{ color: 'text.secondary' }} />
          Devices By Location
        </Typography>
        
        {(() => {
          // Combine location data from all tenants
          const locationMap: Record<string, number> = {};
          let totalDevicesAllTenants = 0;
          
          tenantQueries.forEach((query: any) => {
            if (query.data?.locations) {
              totalDevicesAllTenants += query.data.totalDevices || 0;
              query.data.locations.forEach((location: any) => {
                const city = location.city || 'Unknown';
                const country = location.country || '';
                const locationKey = country && country !== 'Unknown' && country !== city
                  ? `${country} / ${city}`
                  : city;
                
                locationMap[locationKey] = (locationMap[locationKey] || 0) + (location.count || 0);
              });
            }
          });
          
          const combinedLocations = Object.entries(locationMap)
            .map(([location, count]) => ({ location, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
          
          if (combinedLocations.length === 0) {
            return (
              <List>
                <ListItem><ListItemText primary="No location data available" /></ListItem>
              </List>
            );
          }
          
          const left = combinedLocations.slice(0, 5);
          const right = combinedLocations.slice(5, 10);
          
          const renderList = (items: typeof combinedLocations) => (
            <List>
              {items.map((item) => {
                const count = item.count;
                const pct = totalDevicesAllTenants ? ((count / totalDevicesAllTenants) * 100).toFixed(2) : '0.00';
                
                return (
                  <React.Fragment key={item.location}>
                    <ListItem>
                      <ListItemText 
                        primary={item.location} 
                        secondary={`${count} device${count !== 1 ? 's' : ''} • ${pct}%`} 
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                );
              })}
            </List>
          );

          return (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>{renderList(left)}</Box>
              <Box sx={{ flex: 1 }}>{renderList(right)}</Box>
            </Box>
          );
        })()}
      </Paper>
    </>
  );
}
